import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses';
import { ElisaYrityspuheUusiOfferCode } from '../../common/enums';
import {
  additionalInformationMsg,
  elisaKukaSoittaaMsg,
  elisaMobiilivarmenneMsg,
  euMsg,
  exploreAndOrderMsg,
  finlandNordicsAndBalticsMsg2,
  internetConnectionMsg,
  mBpsMsg,
  newMsg,
  outsideEuMsg,
  pcsMsg,
  speedMsg,
  t,
  textMessagesMsg,
  unlimitedInternetInFinlandNordicBalticMsg,
  unlimitedMsg,
  uploadSpeedMsg,
  voiceAndTextMessagesMsg,
  voiceMsg,
} from '../../common/i18n';
import {
  CallsAsPerUsageContent,
  DomesticData0GBContent,
  KukaSoittaaContent,
  MobiiliturvaYrityksilleContent,
  MobiilivarmenneContent,
  ScamCallBlockerContent,
  elisaReissunettiContent,
  reissunetti1GtContent,
  reissunetti2GtContent,
  textMessageContent,
  voiceMinutesContent,
} from './HorizontalSubscriptionsContents';
import { OfferSpecifications } from './OfferSpecifications';
import { HorizontalCardPopOver, SubscriptionCard } from './SubscriptionCard';
import { getSubscription } from './SubscriptionUtils';
import type { ModalDescriptionItem } from './SubscriptionModal';
import type { VoiceSubCardProps } from './types';
import type { JSX } from 'react';

const isUpgradeSpeedSelected = (cardName: string, selectedOfferPerGroup?: Record<string, string>) => {
  const selectedOfferGuid = selectedOfferPerGroup?.[cardName] || '';
  return [
    ElisaYrityspuheUusiOfferCode.VoiceYrityspuhePlus300M,
    ElisaYrityspuheUusiOfferCode.VoiceYrityspuhePro1000M,
  ].some(code => code === selectedOfferGuid);
};

interface VoiceModalDescriptionConfig {
  cardName: string;
  baseSpeed: string;
  upgradedSpeed: string;
  uploadSpeed: string;
  euData: string;
  travelLabel: string;
  travelPopover: JSX.Element;
  includedServices: Array<{ label: string; content: JSX.Element }>;
  additionalInformation?: JSX.Element;
}

const getVoiceModalDescriptionItems = (
  selectedOfferPerGroup: Record<string, string> | undefined,
  activePopoverRef: VoiceSubCardProps['activePopoverRef'],
  config: VoiceModalDescriptionConfig
): ModalDescriptionItem[] => {
  const upgradeSelected = isUpgradeSpeedSelected(config.cardName, selectedOfferPerGroup);

  return [
    {
      title: t.VY1K(voiceAndTextMessagesMsg),
      content: (
        <OfferSpecifications
          specifications={[
            {
              name: t.XBSA(speedMsg),
              value: t.IJNS(mBpsMsg, upgradeSelected ? config.upgradedSpeed : config.baseSpeed),
            },
            { name: t.CQQ7(uploadSpeedMsg), value: t.IJNS(mBpsMsg, config.uploadSpeed) },
            { name: t.PV8D(voiceMsg), value: '5000 min' },
            { name: t.MXR5(textMessagesMsg), value: `100 ${t.B3MG(pcsMsg)}` },
          ]}
          activePopoverRef={activePopoverRef}
        />
      ),
    },
    {
      title: t.N2A4('Included services'),
      content: (
        <div className="of-subscription-modal--included-services">
          {config.includedServices.map((service, index) => (
            <div key={`included-service-${index}`} className={`${dsClass.TEXT_XS} ${dsClass.DISPLAY_FLEX} flex-gap--1`}>
              {service.label}
              <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={service.content} />
            </div>
          ))}
          {!upgradeSelected && (
            <div className={`${dsClass.TEXT_XS} ${dsClass.DISPLAY_FLEX} flex-gap--1`}>
              {t.ZKZZ(elisaKukaSoittaaMsg)}
              <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={<KukaSoittaaContent />} />
            </div>
          )}
        </div>
      ),
    },
    {
      title: t.B10B(internetConnectionMsg),
      content: (
        <OfferSpecifications
          specifications={[
            { name: t.OMY8(finlandNordicsAndBalticsMsg2), value: t.MVTD(unlimitedMsg) },
            { name: t.ZFMI(euMsg), value: t.M09Z('{} GB', config.euData) },
            {
              name: t.DTQ6(outsideEuMsg),
              value: config.travelLabel,
              popoverContent: config.travelPopover,
            },
          ]}
          activePopoverRef={activePopoverRef}
        />
      ),
    },
    ...(config.additionalInformation
      ? [{ title: t.GZUK(additionalInformationMsg), content: config.additionalInformation }]
      : [{}]),
  ];
};

const puhePlusModalDescriptionConfig: VoiceModalDescriptionConfig = {
  cardName: 'puheplus',
  baseSpeed: '100',
  upgradedSpeed: '300',
  uploadSpeed: '50',
  euData: '65',
  travelLabel: `Reissunetti 1${t.C4YQ('GB')}`,
  travelPopover: reissunetti1GtContent(),
  includedServices: [{ label: t.ELMO(elisaMobiilivarmenneMsg), content: <MobiilivarmenneContent /> }],
  additionalInformation: (
    <p className={dsClass.TEXT_XS}>
      {t.N2A5(
        'Calls and text messages exceeding the package, as well as calls to landline numbers, are €0.199/min or per call.'
      )}
    </p>
  ),
};

const puheProModalDescriptionConfig: VoiceModalDescriptionConfig = {
  cardName: 'puhepro',
  baseSpeed: '600',
  upgradedSpeed: '1000',
  uploadSpeed: '100',
  euData: '95',
  travelLabel: `Reissunetti 2${t.C4YQ('GB')}`,
  travelPopover: reissunetti2GtContent(),
  includedServices: [
    { label: t.EH0U('Elisa Mobiiliturva for companies'), content: <MobiiliturvaYrityksilleContent /> },
    { label: t.ELMO(elisaMobiilivarmenneMsg), content: <MobiilivarmenneContent /> },
  ],
};

export const getPuhePlusModalDescriptionItems = (
  selectedOfferPerGroup: Record<string, string> | undefined,
  activePopoverRef: VoiceSubCardProps['activePopoverRef']
) => getVoiceModalDescriptionItems(selectedOfferPerGroup, activePopoverRef, puhePlusModalDescriptionConfig);

export const getPuheProModalDescriptionItems = (
  selectedOfferPerGroup: Record<string, string> | undefined,
  activePopoverRef: VoiceSubCardProps['activePopoverRef']
) => getVoiceModalDescriptionItems(selectedOfferPerGroup, activePopoverRef, puheProModalDescriptionConfig);

export const PuhePlus = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
  onSelectOffer,
  selectedOfferPerGroup,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    showAlternateProductChoice={true}
    subscriptionCard={getSubscription(subscriptionCards, 'puheplus')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[
      <CL.Badge key={1} color="5g" text="5G+" />,
      <CL.Badge key={2} color="attention-subtle" text={t.GNFK(newMsg)} />,
    ]}
    subHeader="Elisa"
    header="Yrityspuhe Plus"
    description={t.TYJR('For worry-free everyday use and active web browsing.')}
    infoItems={[
      {
        icon: 'speed',
        text: t.WXU0('Speed {} Mbps', isUpgradeSpeedSelected('puheplus', selectedOfferPerGroup) ? '300' : '100'),
      },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '65') },
      { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
      { icon: 'block', text: t.CQCX('Scam call blocker'), popoverContent: <ScamCallBlockerContent /> },
    ]}
    productVariantInfoBullets={[
      { icon: 'bullet', text: t.N1X2('Elisa Kuka Soittaa or') },
      { icon: 'bullet', text: t.N1X3('Speed upgrade {} Mbps \u2192 {} Mbps', '100', '300') },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    additionalFeatures={[
      {
        icon: 'check',
        text: t.ELMO(elisaMobiilivarmenneMsg),
        popoverContent: <MobiilivarmenneContent />,
      },
      {
        icon: 'check',
        text: `Reissunetti 1${t.C4YQ('GB')}`,
        popoverContent: reissunetti1GtContent(),
      },
    ]}
    modalDescriptionItems={getPuhePlusModalDescriptionItems(selectedOfferPerGroup, activePopoverRef)}
    onSelectOffer={onSelectOffer}
    showAddonConfiguration={true}
  />
);

export const PuhePro = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
  onSelectOffer,
  selectedOfferPerGroup,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    showAlternateProductChoice={true}
    subscriptionCard={getSubscription(subscriptionCards, 'puhepro')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[
      <CL.Badge key={1} color="5g" text="5G+" />,
      <CL.Badge key={2} color="attention-subtle" text={t.GNFK(newMsg)} />,
    ]}
    subHeader="Elisa"
    header="Yrityspuhe Pro"
    description={t.N1X8('For transferring large files, video conferencing, and efficient use of cloud services.')}
    infoItems={[
      {
        icon: 'speed',
        text: t.WXU0('Speed {} Mbps', isUpgradeSpeedSelected('puhepro', selectedOfferPerGroup) ? '1000' : '600'),
      },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '95') },
      { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
      { icon: 'block', text: t.CQCX('Scam call blocker'), popoverContent: <ScamCallBlockerContent /> },
    ]}
    productVariantInfoBullets={[
      { icon: 'bullet', text: t.N1X2('Elisa Kuka Soittaa or') },
      { icon: 'bullet', text: t.N1X3('Speed upgrade {} Mbps \u2192 {} Mbps', '600', '1000') },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    additionalFeatures={[
      {
        icon: 'check',
        text: t.EH0U('Elisa Mobiiliturva for companies'),
        popoverContent: <MobiiliturvaYrityksilleContent />,
      },
      {
        icon: 'check',
        text: t.ELMO(elisaMobiilivarmenneMsg),
        popoverContent: <MobiilivarmenneContent />,
      },
      {
        icon: 'check',
        text: `Reissunetti 2${t.C4YQ('GB')}`,
        popoverContent: reissunetti2GtContent(),
      },
    ]}
    modalDescriptionItems={getPuheProModalDescriptionItems(selectedOfferPerGroup, activePopoverRef)}
    onSelectOffer={onSelectOffer}
    showAddonConfiguration={true}
  />
);

export const PuhePremium = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    subscriptionCard={getSubscription(subscriptionCards, 'puhepremium')}
    {...sharedProps}
    onOrder={onOrderClick}
    badges={[
      <CL.Badge key={1} color="5g" text="5G+" />,
      <CL.Badge key={2} color="blue-subtle" text={t.GPDS('Most popular')} />,
    ]}
    subHeader="Elisa"
    header="Yrityspuhe Premium"
    description={t.N1X6(
      'The best possible connection for demanding use. Experience ultra-fast transfer of large files, high-quality video conferencing, seamless live streaming, and reliable access to cloud services.'
    )}
    infoItems={[
      { icon: 'speed', text: t.WXU0('Speed {} Mbps', '2000') },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '120') },
      { icon: 'wifi', text: t.SLOI(unlimitedInternetInFinlandNordicBalticMsg) },
      { icon: 'block', text: t.CQCX('Scam call blocker'), popoverContent: <ScamCallBlockerContent /> },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    additionalFeatures={[
      {
        icon: 'check',
        text: t.ZKZZ(elisaKukaSoittaaMsg),
        popoverContent: <KukaSoittaaContent />,
      },
      {
        icon: 'check',
        text: t.EH0U('Elisa Mobiiliturva for companies'),
        popoverContent: <MobiiliturvaYrityksilleContent />,
      },
      {
        icon: 'check',
        text: `Reissunetti 2${t.C4YQ('GB')}`,
        popoverContent: reissunetti2GtContent(),
      },
      {
        icon: 'check',
        text: t.ELMO(elisaMobiilivarmenneMsg),
        popoverContent: <MobiilivarmenneContent />,
      },
    ]}
    modalDescriptionItems={[
      {
        title: t.VY1K(voiceAndTextMessagesMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.XBSA(speedMsg),
                value: t.IJNS(mBpsMsg, '2000'),
              },
              {
                name: t.CQQ7(uploadSpeedMsg),
                value: t.IJNS(mBpsMsg, '200'),
              },
              {
                name: t.PV8D(voiceMsg),
                value: '10 000 min',
              },
              {
                name: t.MXR5(textMessagesMsg),
                value: `1000 ${t.B3MG(pcsMsg)}`,
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
      {
        title: t.N2A4('Included services'),
        content: (
          <div className="of-subscription-modal--included-services">
            <div className={`${dsClass.TEXT_XS} ${dsClass.DISPLAY_FLEX} flex-gap--1`}>
              {t.ZKZZ(elisaKukaSoittaaMsg)}
              <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={<KukaSoittaaContent />} />
            </div>
            <div className={`${dsClass.TEXT_XS} ${dsClass.DISPLAY_FLEX} flex-gap--1`}>
              {t.EH0U('Elisa Mobiiliturva for companies')}
              <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={<MobiiliturvaYrityksilleContent />} />
            </div>
            <div className={`${dsClass.TEXT_XS} ${dsClass.DISPLAY_FLEX} flex-gap--1`}>
              {t.ELMO(elisaMobiilivarmenneMsg)}
              <HorizontalCardPopOver activePopoverRef={activePopoverRef} content={<MobiilivarmenneContent />} />
            </div>
          </div>
        ),
      },
      {
        title: t.B10B(internetConnectionMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.OMY8(finlandNordicsAndBalticsMsg2),
                value: t.MVTD(unlimitedMsg),
              },
              {
                name: t.ZFMI(euMsg),
                value: t.M09Z('{} GB', '120'),
              },
              {
                name: t.DTQ6(outsideEuMsg),
                value: `Reissunetti 2${t.C4YQ('GB')}`,
                popoverContent: reissunetti2GtContent(),
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
      {}, // needed to align items properly
    ]}
    showAddonConfiguration={true}
    analyticsIndex={analyticsIndex}
  />
);

export const PuheBasic = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    subscriptionCard={getSubscription(subscriptionCards, 'puhebasic')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[<CL.Badge key={1} color="attention-subtle" text={t.GNFK(newMsg)} />]}
    subHeader="Elisa"
    header="Yrityspuhe Basic"
    description={t.N1Y1(
      'For light internet use, such as messaging and reading emails. Suitable, for example, for a shared office phone.'
    )}
    infoItems={[
      { icon: 'speed', text: t.WXU0('Speed {} Mbps', '10') },
      { icon: 'phone', text: t.N1Y2('Call charges based on usage'), popoverContent: <CallsAsPerUsageContent /> },
      { icon: 'wifi', text: t.B10C('Unlimited Internet in Finland') },
      { icon: 'block', text: t.CQCX('Scam call blocker'), popoverContent: <ScamCallBlockerContent /> },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    additionalFeatures={[
      {
        icon: 'check',
        text: t.ELMO(elisaMobiilivarmenneMsg),
        popoverContent: <MobiilivarmenneContent />,
      },
    ]}
    modalDescriptionItems={[
      {
        title: t.VY1K(voiceAndTextMessagesMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.XBSA(speedMsg),
                value: t.IJNS(mBpsMsg, '10'),
              },
              {
                name: t.CQQ7(uploadSpeedMsg),
                value: t.IJNS(mBpsMsg, '5'),
              },
              {
                name: t.N1Z9('Puhe käytön mukaan'),
                value: '0,199 €/min',
              },
              {
                name: t.MXR5(textMessagesMsg),
                value: `0,199 €/${t.B3MG(pcsMsg)}`,
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
      {
        title: t.B10B(internetConnectionMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.N2A1('Within Finland'),
                value: t.MVTD(unlimitedMsg),
              },
              {
                name: t.ZFMI(euMsg),
                value: t.M09Z('{} GB', '0'),
              },
              {
                name: t.DTQ6(outsideEuMsg),
                value: t.M09Z('{} GB', '0'),
                popoverContent: <DomesticData0GBContent />,
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
    ]}
    showAddonConfiguration={true}
  />
);

export const PuheLite50m = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    subscriptionCard={getSubscription(subscriptionCards, 'puhelite50m')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[<CL.Badge key={1} color="5g" text="4G" />]}
    subHeader="Elisa"
    header="Yrityspuhe Lite 50M"
    description={t.N1Y3('For light everyday use, web browsing and messaging.')}
    infoItems={[
      { icon: 'speed', text: t.WXU0('Speed {} Mbps', '50') },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '20') },
      { icon: 'phone', text: t.N1Y4('Text messages charges based on usage'), popoverContent: textMessageContent() },
      { icon: 'wifi', text: t.N1Y5('{} voice minutes included', '5000'), popoverContent: voiceMinutesContent('5000') },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    modalDescriptionItems={[
      {
        title: t.VY1K(voiceAndTextMessagesMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.XBSA(speedMsg),
                value: t.IJNS(mBpsMsg, '50'),
              },
              {
                name: t.CQQ7(uploadSpeedMsg),
                value: t.IJNS(mBpsMsg, '25'),
              },
              {
                name: t.PV8D(voiceMsg),
                value: '5000 min',
              },
              {
                name: t.MXR5(textMessagesMsg),
                value: `0,999 €/${t.B3MG(pcsMsg)}`,
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
      {
        title: t.B10B(internetConnectionMsg),
        content: (
          <OfferSpecifications
            specifications={[
              {
                name: t.OMY8(finlandNordicsAndBalticsMsg2),
                value: t.MVTD(unlimitedMsg),
              },
              {
                name: t.ZFMI(euMsg),
                value: t.M09Z('{} GB', '20'),
              },
              {
                name: t.DTQ6(outsideEuMsg),
                value: 'Reissunetti',
                popoverContent: elisaReissunettiContent(),
              },
            ]}
            activePopoverRef={activePopoverRef}
          />
        ),
      },
    ]}
  />
);

export const getPuheLite100mModalDescriptionItems = (
  activePopoverRef: VoiceSubCardProps['activePopoverRef']
): ModalDescriptionItem[] => [
  {
    title: t.VY1K(voiceAndTextMessagesMsg),
    content: (
      <OfferSpecifications
        specifications={[
          { name: t.XBSA(speedMsg), value: t.IJNS(mBpsMsg, '100') },
          { name: t.CQQ7(uploadSpeedMsg), value: t.IJNS(mBpsMsg, '50') },
          { name: t.PV8D(voiceMsg), value: '5000 min' },
          { name: t.MXR5(textMessagesMsg), value: `100 ${t.B3MG(pcsMsg)}` },
        ]}
        activePopoverRef={activePopoverRef}
      />
    ),
  },
  {
    title: t.B10B(internetConnectionMsg),
    content: (
      <OfferSpecifications
        specifications={[
          { name: t.OMY8(finlandNordicsAndBalticsMsg2), value: t.MVTD(unlimitedMsg) },
          { name: t.ZFMI(euMsg), value: t.M09Z('{} GB', '35') },
          { name: t.DTQ6(outsideEuMsg), value: 'Reissunetti', popoverContent: elisaReissunettiContent() },
        ]}
        activePopoverRef={activePopoverRef}
      />
    ),
  },
];

export const PuheLite100m = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    subscriptionCard={getSubscription(subscriptionCards, 'puhelite100m')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[<CL.Badge key={1} color="5g" text="5G" />]}
    subHeader="Elisa"
    header="Yrityspuhe Lite 100M"
    description={t.N2A7('For smooth everyday use, web browsing, and email.')}
    infoItems={[
      { icon: 'speed', text: t.WXU0('Speed {} Mbps', '100') },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '35') },
      { icon: 'phone', text: t.N1Y8('{} text messages included', '100'), popoverContent: textMessageContent() },
      { icon: 'wifi', text: t.N1Y5('{} voice minutes included', '5000'), popoverContent: voiceMinutesContent('5000') },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    modalDescriptionItems={getPuheLite100mModalDescriptionItems(activePopoverRef)}
  />
);

export const getPuheLite300mModalDescriptionItems = (
  activePopoverRef: VoiceSubCardProps['activePopoverRef']
): ModalDescriptionItem[] => [
  {
    title: t.VY1K(voiceAndTextMessagesMsg),
    content: (
      <OfferSpecifications
        specifications={[
          {
            name: t.XBSA(speedMsg),
            value: t.IJNS(mBpsMsg, '300'),
          },
          {
            name: t.CQQ7(uploadSpeedMsg),
            value: t.IJNS(mBpsMsg, '50'),
          },
          {
            name: t.PV8D(voiceMsg),
            value: '5000 min',
          },
          {
            name: t.MXR5(textMessagesMsg),
            value: `100 ${t.B3MG(pcsMsg)}`,
          },
        ]}
        activePopoverRef={activePopoverRef}
      />
    ),
  },
  {
    title: t.B10B(internetConnectionMsg),
    content: (
      <OfferSpecifications
        specifications={[
          {
            name: t.OMY8(finlandNordicsAndBalticsMsg2),
            value: t.MVTD(unlimitedMsg),
          },
          {
            name: t.ZFMI(euMsg),
            value: t.M09Z('{} GB', '50'),
          },
          {
            name: t.DTQ6(outsideEuMsg),
            value: `Reissunetti 1${t.C4YQ('GB')}`,
            popoverContent: reissunetti1GtContent(),
          },
        ]}
        activePopoverRef={activePopoverRef}
      />
    ),
  },
];

export const PuheLite300m = ({
  analyticsIndex,
  subscriptionCards,
  sharedProps,
  onOrderClick,
  activePopoverRef,
}: VoiceSubCardProps) => (
  <SubscriptionCard
    subscriptionCard={getSubscription(subscriptionCards, 'puhelite300m')}
    {...sharedProps}
    onOrder={onOrderClick}
    analyticsIndex={analyticsIndex}
    badges={[<CL.Badge key={1} color="5g" text="5G" />]}
    subHeader="Elisa"
    header="Yrityspuhe Lite 300M"
    description={t.N1Z1('For worry-free everyday use, web browsing, and video calls.')}
    infoItems={[
      { icon: 'speed', text: t.WXU0('Speed {} Mbps', '300') },
      { icon: 'globe', text: t.IJ5P('EU data {} GB/month', '75') },
      { icon: 'phone', text: t.N1Y8('{} text messages included', '100'), popoverContent: textMessageContent() },
      { icon: 'wifi', text: t.N1Y5('{} voice minutes included', '5000'), popoverContent: voiceMinutesContent('5000') },
    ]}
    hasQuantity={false}
    buttonText={t.SDWF(exploreAndOrderMsg)}
    additionalFeatures={[
      {
        icon: 'check',
        text: 'Elisa Reissunetti',
        popoverContent: reissunetti1GtContent(),
      },
    ]}
    modalDescriptionItems={getPuheLite300mModalDescriptionItems(activePopoverRef)}
  />
);
